import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  VideoCameraIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SessionInfo {
  appointment_id: string;
  coach_client_id: string;
  client_name: string;
  scheduled_at: string | null;
  meeting_url: string | null;
  meeting_name: string | null;
}

interface ClientNote {
  id: string;
  coach_client_id: string;
  appointment_id: string | null;
  content: string;
  created_at: string;
  updated_at: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildJoinUrl(meetingUrl: string, appointmentId?: string, name?: string): string {
  const params = new URLSearchParams({ meeting_url: meetingUrl });
  if (appointmentId) params.set('appointment_id', appointmentId);
  if (name) params.set('name', name);
  return `/session/join?${params.toString()}`;
}

function openJoinWindow(url: string): void {
  const w = Math.round(window.screen.width * 0.8);
  const h = Math.round(window.screen.height * 0.8);
  const left = Math.round((window.screen.width - w) / 2);
  const top = Math.round((window.screen.height - h) / 2);
  window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SessionCockpitPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();

  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newContent, setNewContent] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const editRef = useRef<HTMLTextAreaElement>(null);


  // ── Load session info + notes ─────────────────────────────────────────────

  useEffect(() => {
    if (!appointmentId) return;
    setLoading(true);

    const headers = authHeaders();

    Promise.all([
      fetch(`${config.apiBaseUrl}${endpoints.main.session.info(appointmentId)}`, { headers }),
      // notes loaded after we have coach_client_id — handled below
    ])
      .then(async ([infoRes]) => {
        if (!infoRes.ok) throw new Error('Session not found');
        const info: SessionInfo = await infoRes.json();
        setSessionInfo(info);

        const notesRes = await fetch(
          `${config.apiBaseUrl}${endpoints.main.clientNotes.list(info.coach_client_id)}`,
          { headers },
        );
        if (!notesRes.ok) throw new Error('Could not load notes');
        const data = await notesRes.json();
        // Show only notes for this session
        setNotes((data.notes as ClientNote[]).filter(n => n.appointment_id === appointmentId));
      })
      .catch(e => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [appointmentId]);

  // ── Join meeting ──────────────────────────────────────────────────────────

  const openJoin = () => {
    if (!sessionInfo?.meeting_url) return;
    openJoinWindow(buildJoinUrl(sessionInfo.meeting_url, appointmentId, sessionInfo.meeting_name || undefined));
  };

  // ── Create note ───────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!newContent.trim() || !sessionInfo) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.clientNotes.create(sessionInfo.coach_client_id)}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ content: newContent.trim(), appointment_id: appointmentId }),
        },
      );
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setNotes(prev => [data.note, ...prev]);
      setNewContent('');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit note ─────────────────────────────────────────────────────────────

  const startEdit = (note: ClientNote) => {
    setEditingId(note.id);
    setEditContent(note.content);
    setTimeout(() => editRef.current?.focus(), 50);
  };

  const cancelEdit = () => { setEditingId(null); setEditContent(''); };

  const saveEdit = async (note: ClientNote) => {
    if (!editContent.trim() || !sessionInfo) return;
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.clientNotes.update(sessionInfo.coach_client_id, note.id)}`,
        {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ content: editContent.trim() }),
        },
      );
      if (!res.ok) throw new Error('Update failed');
      const data = await res.json();
      setNotes(prev => prev.map(n => n.id === note.id ? data.note : n));
      setEditingId(null);
    } catch {
      // leave edit open on error
    }
  };

  // ── Delete note ───────────────────────────────────────────────────────────

  const handleDelete = async (note: ClientNote) => {
    if (!sessionInfo) return;
    if (!window.confirm('Delete this note?')) return;
    await fetch(
      `${config.apiBaseUrl}${endpoints.main.clientNotes.delete(sessionInfo.coach_client_id, note.id)}`,
      { method: 'DELETE', headers: authHeaders() },
    );
    setNotes(prev => prev.filter(n => n.id !== note.id));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !sessionInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-red-600">{error || 'Session not found'}</p>
        <button onClick={() => navigate(-1)} className="text-indigo-600 hover:underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              title="Back"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{sessionInfo.client_name}</h1>
              {sessionInfo.scheduled_at && (
                <p className="text-sm text-gray-500">{formatDateTime(sessionInfo.scheduled_at)}</p>
              )}
              {sessionInfo.meeting_name && (
                <p className="text-xs text-gray-400">{sessionInfo.meeting_name}</p>
              )}
            </div>
          </div>

          {sessionInfo.meeting_url && (
            <button
              onClick={openJoin}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <VideoCameraIcon className="w-4 h-4" />
              Join Meeting
            </button>
          )}
        </div>
      </div>

      {/* Notes panel */}
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h2 className="text-base font-semibold text-gray-700 mb-3">Session Notes</h2>

          {/* New note input */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <textarea
              value={newContent}
              onChange={e => setNewContent(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleCreate();
              }}
              placeholder="Type a note... (Cmd+Enter to save)"
              rows={4}
              className="w-full text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleCreate}
                disabled={saving || !newContent.trim()}
                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                {saving ? 'Saving...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>

        {/* Existing notes */}
        {notes.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No notes for this session yet.</p>
        ) : (
          <div className="space-y-3">
            {notes.map(note => (
              <div key={note.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                {editingId === note.id ? (
                  <>
                    <textarea
                      ref={editRef}
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      rows={4}
                      className="w-full text-sm text-gray-800 resize-none focus:outline-none"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={cancelEdit}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
                        title="Cancel"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => saveEdit(note)}
                        className="p-1.5 rounded-lg hover:bg-green-50 text-green-600"
                        title="Save"
                      >
                        <CheckIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-400">
                        {new Date(note.created_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => startEdit(note)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
                          title="Edit"
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(note)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                          title="Delete"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
